import type { PbxSettingsLocale } from './en'

export const pbxSettingsKo = {
  loading: 'PBX 설정을 불러오는 중...',
  invalidPbx: {
    title: '유효하지 않은 PBX',
    description:
      'PBX 설정을 수정하려면 유효한 PBX ID가 필요합니다. 대시보드에서 PBX를 선택해 주세요.',
  },
  loadError: {
    title: 'PBX 설정을 불러올 수 없습니다',
    description: '다시 시도해 주세요.',
  },
  partialLoadWarning: {
    title: '일부 데이터를 불러오지 못했습니다',
    unknownError: '알 수 없는 오류',
    labels: {
      providerGateways: '공급자 게이트웨이',
      serverGroups: '서버 그룹',
      phoneNumbers: '전화번호',
      virtualFaxes: '가상 팩스',
      alert911Options: '911 옵션',
    },
  },
  actions: {
    update: '업데이트',
  },
  common: {
    loading: '불러오는 중...',
    select: '선택',
  },
  sections: {
    core: {
      title: 'PBX 설정',
    },
    advanced: {
      title: '고급 PBX 설정',
    },
    system: {
      title: '시스템 설정',
    },
  },
  placeholders: {
    selectFromList: '목록에서 선택...',
    selectServerGroup: '서버 그룹 선택',
    selectTimeZone: '시간대 선택',
    selectExtensionDigits: '내선 자릿수 선택',
    selectCodec: '코덱 선택',
  },
  fields: {
    pbxDisplayName: {
      label: 'PBX 표시 이름',
    },
    company: {
      label: '회사',
    },
    providerGateway: {
      label: '공급자 게이트웨이',
    },
    emergencyProviderGateway: {
      label: '긴급 공급자 게이트웨이',
    },
    internationalProviderGateway: {
      label: '국제 공급자 게이트웨이',
    },
    active: {
      label: '활성',
    },
    smsSenderName: {
      label: 'SMS 발신자 이름',
      copyFromCompany: '회사에서 복사',
      helperText:
        'SMS 수신 거부 응답에서 고객에게 표시됩니다(예: "{{name}}: 수신 거부가 완료되었습니다."). 위치별로 설정되며, 회사 이름과 다른 경우 여기서 수정하세요.',
      divergenceHint:
        '회사 이름 "{{companyName}}"과(와) 다릅니다 — 다중 위치 고객의 경우 정상입니다.',
    },
    serverGroup: {
      label: '서버 그룹',
    },
    advanced: {
      maximum_allowed_channels: '최대 채널',
      maximum_lines_in: '수신 채널',
      maximum_lines_out: '내선당 채널',
      call_log_retention_period_in_months: '통화 기록(개월)',
    },
    industry: {
      label: '업종',
    },
    ehr: {
      label: 'EHR',
      helperText: '보고용 정보이며 기능에는 영향을 주지 않습니다',
    },
    defaultEmergencyAlert: {
      label: '기본 긴급 알림',
    },
    defaultTimeZone: {
      label: '기본 시간대',
      britishColumbia: '태평양 시간 - 브리티시컬럼비아 (PCT)',
    },
    callParkTimeout: {
      label: '호출 파크 타임아웃',
    },
    extensionDigits: {
      label: '내선 자릿수',
    },
    defaultAreaCode: {
      label: '기본 지역번호',
    },
    defaultCallerId: {
      label: '기본 발신번호',
    },
    defaultFaxNumber: {
      label: '기본 팩스 번호',
    },
    defaultVirtualFax: {
      label: '기본 가상 팩스',
    },
    preferredCodec: {
      label: '선호 코덱',
    },
    checkboxes: {
      enable_inbound_recording: '수신 통화 녹음',
      enable_outbound_recording: '발신 통화 녹음',
      enable_internal_recording: '내부 통화 녹음',
      international: '국제',
      enable_vm_transcription: '음성사서함 전사',
      schedules_v2: '스케줄 V2',
      enable_agent_statuses: '콜센터',
      enable_call_analytics: 'Mango AI',
      is_grove: 'Grove',
    },
  },
  companySelect: {
    placeholder: '회사 선택',
    ariaLabel: '회사 선택기',
    searchPlaceholder: '회사 검색...',
    empty: {
      loading: '회사를 불러오는 중...',
      noResults: '회사를 찾을 수 없습니다',
      typeToSearch: '검색할 회사를 입력하세요',
    },
  },
  phoneSelect: {
    placeholder: '목록에서 선택...',
    ariaLabel: '전화번호 선택기',
    searchPlaceholder: '전화번호 검색...',
    empty: {
      loading: '전화번호를 불러오는 중...',
      noResults: '전화번호를 찾을 수 없습니다',
      noData: '사용 가능한 전화번호가 없습니다',
    },
  },
  toast: {
    validationError: '유효성 검사 오류',
    timezoneChanged: {
      title: '시간대가 변경되었습니다',
      description:
        'PBX 시간대를 변경했습니다. 스케줄도 함께 업데이트해 주세요.',
    },
    noChanges: '저장할 변경 사항이 없습니다',
    saveSuccess: 'PBX 설정이 업데이트되었습니다',
    saveError: {
      title: '업데이트 실패',
      description: 'PBX 설정 업데이트 중 문제가 발생했습니다.',
    },
  },
  callParkConfirm: {
    title: '호출 파크 타임아웃 확인',
    description: '발신자를 10분 이상 파크 상태로 유지하시겠습니까?',
    cancel: '타임아웃 재설정',
    confirm: '계속',
  },
  rebuildAndReboot: {
    title: 'PBX 설정이 업데이트되었습니다',
    description:
      '일부 PBX 설정은 장치를 다시 빌드하고 재부팅할 때까지 적용되지 않을 수 있습니다.',
    cancel: '나중에',
    confirm: '장치 다시 빌드 및 재부팅',
    toast: {
      successTitle: '다시 빌드 및 재부팅 시작됨',
      successDescription: '모든 장치를 다시 빌드하고 재부팅하는 중입니다.',
      errorTitle: '다시 빌드 및 재부팅 실패',
      errorDescription:
        '장치를 다시 빌드하고 재부팅하는 중 문제가 발생했습니다.',
    },
  },
  validation: {
    pbxDisplayNameRequired: 'PBX 표시 이름을 입력해 주세요.',
    companyRequired: '회사를 선택해 주세요.',
    smsSenderNameRequired: 'SMS 발신자 이름을 입력해 주세요.',
    serverGroupRequired: '서버 그룹을 선택해 주세요.',
    primaryOutboundGatewayRequired: '기본 발신 게이트웨이를 선택해 주세요.',
    primaryInternationalGatewayRequired:
      '기본 국제 게이트웨이를 선택해 주세요.',
    emergencyGatewayRequired: '긴급 게이트웨이를 선택해 주세요.',
    industryRequired: '업종을 선택해 주세요.',
    maxChannelsRequired: '최대 채널 값을 입력해 주세요.',
    inboundChannelsRequired: '수신 채널 값을 입력해 주세요.',
    perExtensionChannelsRequired: '내선당 채널 값을 입력해 주세요.',
    callHistoryRequired: '통화 기록 값을 입력해 주세요.',
    numericFieldRange: '{{field}} 값은 {{min}}에서 {{max}} 사이여야 합니다.',
    numericFieldMin: '{{min}} 이상이어야 합니다.',
    numericFieldMax: '{{max}} 이하여야 합니다.',
    defaultTimeZoneRequired: '기본 시간대를 선택해 주세요.',
    callParkTimeoutRange:
      '호출 파크 타임아웃은 {{min}}-{{max}} 사이로 입력해 주세요(권장: {{recommended}}).',
    callParkTimeoutMax: '호출 파크 타임아웃은 {{max}}초를 초과할 수 없습니다.',
    extensionDigitsRequired: '내선 자릿수를 입력해 주세요.',
    extensionDigitsInvalid: '내선 자릿수는 3, 4 또는 5여야 합니다.',
    defaultAreaCodeRequired: '기본 지역번호를 입력해 주세요.',
    defaultAreaCodeDigits: '기본 지역번호는 3자리 숫자여야 합니다.',
    defaultAreaCodeRange: '기본 지역번호는 200~998 사이여야 합니다.',
    defaultAreaCodeDisallowed:
      '지역번호 {{code}}는 기본 지역번호로 사용할 수 없습니다.',
    defaultCallerIdRequired: '기본 발신번호를 선택해 주세요.',
    defaultFaxNumberRequired: '기본 팩스 번호를 선택해 주세요.',
    defaultVirtualFaxRequired: '기본 가상 팩스를 선택해 주세요.',
    preferredCodecRequired: '선호 코덱을 선택해 주세요.',
  },
} satisfies PbxSettingsLocale
